import {
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  inject
} from '@angular/core';
import { Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { ButtonLoaderBase } from '../components/button/button-loader.component';
import { ButtonBase } from '../components/button/button.component';

@Directive({
    selector: 'button[simFireOnClick], a[simFireOnClick]',
    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
    inputs: ['loading'],
    standalone: false
})
export class FireOnClickDirective implements OnDestroy {
  private readonly hostElement = inject(ElementRef);
  private readonly buttonComponent = inject(ButtonBase, { self: true });
  private readonly _destroySource: Subject<void> = new Subject<void>();

  @HostBinding('class.sim-loading-button')
  get uiButtonLoading(): boolean {
    return this.buttonComponent.loading ?? false;
  }

  /**
   * Example:
   *
   * <button ui-raised-button [uiFireOnClick]="something$">
   * <ui-button-loader [direction]="'end'"></ui-button-loader>
   * Click me
   * <i ui-font fontName="assets" fontSize="2rem" [direction]="'begin'"></i>
   * </button>
   */
  @Input()
  set uiFireOnClick(stream$: Observable<unknown>) {
    this._fireOnClick$ = stream$;
  }
  private _fireOnClick$!: Observable<unknown>;

  @ContentChild(ButtonLoaderBase)
  set buttonLoaderRef(buttonLoaderBase: ButtonLoaderBase) {
    if (!buttonLoaderBase) {
      throw new Error('sim-button-loading is missing inside button element!');
    }

    this._buttonLoaderBase = buttonLoaderBase;
    const hostElementHeight = (this.hostElement.nativeElement as HTMLElement).clientHeight;
    this._buttonLoaderBase.setHeight(hostElementHeight);
  }
  private _buttonLoaderBase!: ButtonLoaderBase;

  @HostListener('click', ['$event.target']) onClick(): void {
    if (!this._fireOnClick$) {
      throw new Error('given stream is invalid!');
    }
    this.buttonComponent.loadingBegin();
    this._fireOnClick$
      .pipe(
        tap(() => {
          this.buttonComponent.loadingEnd();
          //TODO: mby in the feature we will need to have @Output?
        }),
        takeUntil(this._destroySource),
        take(1)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroySource.next();
    this._destroySource.complete();
  }
}
